import React, { useEffect, useRef, useMemo } from 'react'
import { ConsentState } from '../../client/utils/onetrust-utils'
import { useMetaAdsConversionTags } from './ads'
import { useRestaurant } from '@local/do-secundo-restaurant-provider'

interface FacebookPixelProps {
  consentState: ConsentState
  useLimitedDataUse?: boolean
}

export const FacebookPixel: React.FC<FacebookPixelProps> = ({
  consentState,
  useLimitedDataUse = false
}) => {
  const isInitialized = useRef(false)
  const { data: metaData } = useMetaAdsConversionTags()
  const { applicableConfigs } = useRestaurant()
  const { pixelsConfig } = applicableConfigs

  // Collect all Facebook pixel IDs - memoized to prevent unnecessary re-renders
  const pixelIds = useMemo(() => {
    const ids: string[] = []
    if (metaData?.pixelId) {
      ids.push(metaData.pixelId)
    }
    if (pixelsConfig?.facebook) {
      ids.push(pixelsConfig.facebook)
    }
    return ids
  }, [metaData?.pixelId, pixelsConfig?.facebook])

  useEffect(() => {
    // Facebook Pixel requires Targeting consent
    if (
      !consentState.hasTargetingConsent ||
      pixelIds.length === 0 ||
      isInitialized.current
    )
      return

    // Only initialize once
    if (!window.fbq) {
      // Use Facebook's exact initialization code via script injection
      const script = document.createElement('script')
      script.innerHTML = `
        !function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod ?
          n.callMethod.apply(n, arguments) : n.queue.push(arguments)
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s)
    }(window, document, 'script',
      'https://connect.facebook.net/en_US/fbevents.js');`
      document.head.appendChild(script)
    }

    // Wait for fbq to be available
    const maxAttempts = 100
    let attempts = 0
    const waitForFbq = setInterval(() => {
      attempts++
      const { fbq } = window

      if (fbq) {
        clearInterval(waitForFbq)

        // Set Limited Data Use if needed
        if (useLimitedDataUse) {
          fbq('dataProcessingOptions', ['LDU'], 0, 0)
        }

        // Initialize each pixel ID
        pixelIds.forEach((pixelId) => {
          fbq('init', pixelId)
        })

        // Track page view
        fbq('track', 'PageView')

        isInitialized.current = true
      } else if (attempts >= maxAttempts) {
        console.warn(
          'Facebook Pixel: Stopped waiting for fbq after max attempts'
        )
        clearInterval(waitForFbq)
      }
    }, 100)

    // Cleanup
    return () => {
      clearInterval(waitForFbq)
    }
  }, [consentState.hasTargetingConsent, pixelIds, useLimitedDataUse])

  // Handle consent changes for already initialized Facebook Pixel
  useEffect(() => {
    const { fbq } = window
    if (!fbq) return

    if (consentState.hasTargetingConsent && useLimitedDataUse) {
      // Enable data processing with Limited Data Use
      fbq('dataProcessingOptions', ['LDU'], 0, 0)
    } else if (!consentState.hasTargetingConsent) {
      // Disable data processing when consent is revoked
      fbq('dataProcessingOptions', [''])
    }
  }, [consentState.hasTargetingConsent, useLimitedDataUse])

  return null
}
